/**
 * Vylo Storage — platform schemas.
 *
 * Backs the /api/v1/platform/storage surface: the platform owner's dashboard
 * for the object-storage service at storage.vylo-tech.com, which keeps its
 * bytes on a Hetzner Storage Box and serves them from cdn.vylo-tech.com.
 *
 * One customer = one zone = one directory on the box and one key. The service
 * has its own database and its own admin token; nothing here duplicates that
 * state, it is read through and shown.
 */

import { z } from 'zod';

// -----------------------------------------------------------------------------
// Inputs
// -----------------------------------------------------------------------------

/** A zone name is also a URL path segment and a directory on the box, so the
 *  rule is the storage service's own and is repeated here to fail in the
 *  browser rather than after a round trip. */
export const StorageZoneNameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3)
  .max(32)
  .regex(
    /^[a-z0-9][a-z0-9-]*[a-z0-9]$/,
    'lowercase letters, digits and hyphens; must start and end with a letter or digit',
  );

/**
 * 0 means no ceiling, which is the normal case now that storage is sold by
 * the terabyte with no cap. The upper bound is only a typo guard — 100 TB is
 * far past anything real, and a fat-fingered 999999 should not silently
 * become somebody's quota.
 */
export const StorageQuotaGbSchema = z.number().min(0).max(102_400);

export const StorageZoneCreateSchema = z.object({
  name: StorageZoneNameSchema,
  /** true = served without a signed link, for a zone that is meant to be public. */
  isPublic: z.boolean().default(false),
  quotaGb: StorageQuotaGbSchema.default(0),
  /** Free text shown in the platform list — who this zone is for. */
  label: z.string().trim().max(120).optional(),
});
export type StorageZoneCreate = z.infer<typeof StorageZoneCreateSchema>;

export const StorageZoneUpdateSchema = z
  .object({
    isPublic: z.boolean().optional(),
    quotaGb: StorageQuotaGbSchema.optional(),
  })
  .refine((v) => v.isPublic !== undefined || v.quotaGb !== undefined, {
    message: 'nothing to change',
  });
export type StorageZoneUpdate = z.infer<typeof StorageZoneUpdateSchema>;

// -----------------------------------------------------------------------------
// Responses (mapped server-side from the storage service's shapes)
// -----------------------------------------------------------------------------

/** One customer's zone, with what it is holding right now. */
export interface StorageZone {
  name: string;
  isPublic: boolean;
  /** 0 when there is no ceiling. */
  quotaBytes: number;
  usedBytes: number;
  objects: number;
  /** Uploaded but not yet on the box — normally 0, briefly not. */
  pending: number;
  /** null when there is no quota to be a percentage of. */
  quotaUsedPct: number | null;
  createdAt: number;
}

/** The key is shown once, here, and only its hash is kept by the service. */
export interface StorageZoneCreated extends StorageZone {
  key: string;
}

/** A rotated key. The previous one stopped working when this was issued. */
export interface StorageZoneKey {
  name: string;
  key: string;
}

/** GET /config — `configured` is false (not an error) when the env vars are unset. */
export interface StorageConfig {
  configured: boolean;
  serverUrl: string | null;
  /** Where a public or signed link points, for the copy-link buttons. */
  cdnUrl: string | null;
  /** Whether the storage service answered at all. */
  reachable: boolean;
  /** Whether IT can reach the Storage Box — a different failure, and the
   *  one that means an upload would be accepted and then stuck in a queue. */
  boxReachable: boolean;
  detail: string | null;
  disk: StorageDisk | null;
  cache: StorageCache | null;
  queue: StorageQueue | null;
}

/** Free space on the delivery server. `acceptingUploads` false means every
 *  upload is being refused with 507 — the disk floor, not a fault. */
export interface StorageDisk {
  freeBytes: number;
  floorBytes: number;
  acceptingUploads: boolean;
}

export interface StorageCache {
  bytes: number;
  limitBytes: number;
  usedPct: number | null;
}

/** Objects waiting to reach the box, by state ('spooled' | 'stored' | 'failed'). */
export interface StorageQueue {
  spooled: number;
  uploading: number;
  stored: number;
  failed: number;
}
